import type { LucideIcon } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MessageCircle,
  MessagesSquare,
  User,
  LogOut,
  Settings,
  LifeBuoy,
  BookOpen,
  ChartNoAxesColumnIncreasing,
  FileText,
  LibraryBig,
  Scale,
  Briefcase,
  ClipboardCheck,
  DraftingCompass,
  Handshake,
  ChevronRight,
  Link2,
  FolderOpen,
  FlaskConical,
  Pencil,
  ClipboardList,
  LayoutDashboard,
  Users,
  FolderKanban,
} from "lucide-react";
import type { ActiveSection } from "@/types/appNavigation";
import { ConversationsList } from "@/components/chat/ConversationsList";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar as SidebarRoot,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useTheme } from "@/components/ui/theme-provider";
import { OrdinalIcon } from "./OrdinalIcon";
import { OrdinalLogo } from "./OrdinalLogo";
import { OrdinalLogoDark } from "./OrdinalLogoDark";
import clsx from "clsx";
import AccountDialog from "./AccountDialog";
import { UsersSettingsModal } from "@/components/team_management/UsersSettingsModal";
import { SupportRequestDialog } from "@/components/launch_pad/SupportRequestDialog";
import DocumentsManager from "./DocumentsManager";
import { useUser } from "@/context/UserProvider";
import { useMunicipality } from "@/context/MunicipalityContext";
import { ActionAlertsBell } from "@/components/action_alerts/ActionAlertsBell";
import { useGrantSuiteNavigation } from "@/context/GrantSuiteNavigationContext";
import { usePermitSuiteNavigation } from "@/context/PermitSuiteNavigationContext";
import { userHasOutsideShell } from "@/lib/grantSuiteAccess";
import { canAccessPermitTab } from "@/lib/permitSuiteAccess";
import { userHasDevelopmentSuite } from "@/lib/developmentSuiteAccess";
import {
  documentManagementEnabled,
  documentManagementFlagged,
  teamManagementEnabled,
} from "@/lib/adminSettings";

/** Reserved for Phase 2 when platform_support gains Madmin access. */
export const SUPPORT_CONSOLE_LABEL = "Support Console";
export const OPS_CONSOLE_LABEL = "Ops Console";
import { CaseHistoryList } from "@/components/case_portal/CaseHistoryList";
import { CodeLibraryComparisonsList } from "@/components/code_library/CodeLibraryComparisonsList";
import { SandboxConversationsList } from "@/components/sandbox/SandboxConversationsList";
import { useStaffReports } from "@/context/StaffReportsContext";

type HistorySection = "chat" | "code_library" | "case_portal" | "sandbox";

/** Shared layout for top-level sidebar nav rows (flat links and history triggers). */
const SIDEBAR_NAV_BUTTON_CLASS = "h-9 font-medium px-3 py-2 text-sm";

const SIDEBAR_NAV_COLLAPSED_CLASS = "justify-center gap-0 px-0";

interface SidebarNavItemProps {
  collapsed: boolean;
  icon: LucideIcon;
  label: string;
  isActive: boolean;
  onSelect: () => void;
  testId?: string;
}

function SidebarNavItem({
  collapsed,
  icon: Icon,
  label,
  isActive,
  onSelect,
  testId,
}: SidebarNavItemProps) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={isActive}
        className={cn(SIDEBAR_NAV_BUTTON_CLASS, collapsed && SIDEBAR_NAV_COLLAPSED_CLASS)}
        onClick={onSelect}
        data-testid={testId}
        aria-label={collapsed ? label : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" aria-hidden />
        {!collapsed && <span className="truncate">{label}</span>}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

interface SidebarProps {
  activeSection?: ActiveSection;
  setActiveSection?: (section: ActiveSection) => void;
}

interface HistoryModuleRowProps {
  collapsed: boolean;
  icon: typeof MessageCircle;
  id: HistorySection;
  isActive: boolean;
  isOpen: boolean;
  label: string;
  onOpenChange: (open: boolean) => void;
  onSelect: () => void;
  children: React.ReactNode;
}

function HistoryModuleRow({
  collapsed,
  icon: Icon,
  id,
  isActive,
  isOpen,
  label,
  onOpenChange,
  onSelect,
  children,
}: HistoryModuleRowProps) {
  const isExpanded = !collapsed && isActive && isOpen;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (collapsed) {
      event.preventDefault();
      onOpenChange(true);
      onSelect();
      return;
    }

    if (!isActive) {
      event.preventDefault();
      onOpenChange(true);
      onSelect();
    }
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onOpenChange}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={isActive}
            className={cn(
              SIDEBAR_NAV_BUTTON_CLASS,
              collapsed ? SIDEBAR_NAV_COLLAPSED_CLASS : "justify-between gap-0",
            )}
            data-testid={`${id}-history-trigger`}
            onClick={handleClick}
            aria-label={collapsed ? label : undefined}
          >
            {collapsed ? (
              <Icon className="h-5 w-5 shrink-0" aria-hidden />
            ) : (
              <>
                <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                  <Icon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="truncate">{label}</span>
                </span>
                <ChevronRight
                  className={cn(
                    "ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200",
                    isExpanded && "rotate-90",
                  )}
                  aria-hidden
                />
              </>
            )}
          </SidebarMenuButton>
        </CollapsibleTrigger>
        {isExpanded && (
          <CollapsibleContent>
            <SidebarMenuSub className="ml-0 mt-1 gap-0 pl-1" data-testid={`${id}-history-content`}>
              <SidebarMenuSubItem>{children}</SidebarMenuSubItem>
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}

const FOOTER_MENU_CLOSE_DELAY_MS = 150;
const SIDEBAR_EXPANDED_CONTENT_WIDTH_CLASS = "w-[236px] max-w-[236px]";
const SIDEBAR_EXPANDED_LOGO_CLASS = "h-12 w-auto max-w-[180px]";

function FooterHoverMenu({
  collapsed,
  icon: Icon,
  label,
  testId,
  children,
}: {
  collapsed: boolean;
  icon: LucideIcon;
  label: string;
  testId: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, FOOTER_MENU_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => cancelClose();
  }, []);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          requestAnimationFrame(() => {
            (document.activeElement as HTMLElement)?.blur();
          });
        }
      }}
      modal={false}
    >
      <div
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "h-9 w-full justify-start px-3 py-0 focus-visible:ring-0 focus-visible:ring-offset-0",
              collapsed && "justify-center px-0"
            )}
            data-testid={testId}
            onClick={() => {
              cancelClose();
              setOpen(true);
            }}
          >
            <Icon className="h-5 w-5" />
            <span className={cn("ml-2", collapsed && "sr-only")}>{label}</span>
          </Button>
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent
        side="right"
        sideOffset={16}
        align="start"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        className="min-w-[11rem]"
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function clearSettingsSearchParam(params: URLSearchParams) {
  params.delete("settings");
  const query = params.toString();
  const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  // Keep Inertia's history.state.page. Replacing it with {} makes a later
  // back navigation look like a missing history item and full-page visit.
  window.history.replaceState(window.history.state, "", nextUrl);
}

export function Sidebar({ activeSection, setActiveSection }: SidebarProps) {
  const { collapsed, setCollapsed } = useSidebar();
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [showSupportRequest, setShowSupportRequest] = useState(false);
  const [showDocumentsManager, setShowDocumentsManager] = useState(false);
  const { theme } = useTheme();
  const { currentUser, logout } = useUser();
  const { workspaceView, beforeLeaveReportRef } = useStaffReports();
  const outsideShell = userHasOutsideShell(currentUser);
  const {
    municipality,
    isLoading: municipalitiesLoading,
    selectionResolved,
  } = useMunicipality();
  const { handleActionAlertSelect: handleGrantActionAlertSelect } = useGrantSuiteNavigation();
  const { handleActionAlertSelect: handlePermitActionAlertSelect, navigateToPermitTab } =
    usePermitSuiteNavigation();

  const documentsFlagged = documentManagementFlagged(municipality);
  const canManageDocuments = documentManagementEnabled(municipality);
  const showUsersTool = teamManagementEnabled(municipality);
  // selectionResolved waits for the stored city, so a multi-city list does not
  // count as "none selected" during the commit before that restore.
  const showTeamManagementFooter =
    Boolean(currentUser?.can_manage_team_users) &&
    !municipalitiesLoading &&
    selectionResolved &&
    (municipality == null || !showUsersTool);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("settings") !== "users") return;
    if (!currentUser?.can_manage_team_users) return;
    if (!selectionResolved) return;

    // Open even when the selected city is unflagged. The redirect fires when
    // every manageable team is flagged, which can disagree with the active
    // city, and clearing the param without opening sends the footer link
    // around again. The roster itself only includes flagged teams.
    setShowUsersDialog(true);
    clearSettingsSearchParam(params);
  }, [currentUser?.can_manage_team_users, selectionResolved]);

  const [internalSection, setInternalSection] = useState<ActiveSection>("dashboard");
  const [expandedHistory, setExpandedHistory] = useState<Record<HistorySection, boolean>>({
    chat: true,
    code_library: true,
    case_portal: false,
    // Open by default: the Launch Pad can activate Sandbox without a sidebar
    // click, and New conversation lives only in this list.
    sandbox: true,
  });
  const section = activeSection ?? internalSection;
  const changeSection = (nextSection: ActiveSection) => {
    setActiveSection?.(nextSection);
    if (activeSection === undefined) {
      setInternalSection(nextSection);
    }
  };

  const selectSection = async (nextSection: ActiveSection) => {
    if (section === "staff_reports" && workspaceView === "report" && nextSection !== section && beforeLeaveReportRef.current && !await beforeLeaveReportRef.current(true)) return;
    if (collapsed) {
      setCollapsed(false);
    }
    changeSection(nextSection);
  };

  return (
    <SidebarRoot>
      <aside className="flex h-full flex-col w-min-16">
        <SidebarHeader>
          <div
            className={clsx(
              collapsed && "relative left-3",
              "w-full flex items-center justify-between",
              collapsed ? "left-0" : "left-3"
            )}
          >
            <a className="w-fit" href="/">
              {collapsed ? (
                <OrdinalIcon className="h-10 w-10" />
              ) : theme === "dark" ? (
                <OrdinalLogoDark className={SIDEBAR_EXPANDED_LOGO_CLASS} />
              ) : (
                <OrdinalLogo className={SIDEBAR_EXPANDED_LOGO_CLASS} />
              )}
              <span className="sr-only">Ordinal homepage</span>
            </a>
            <SidebarTrigger />
          </div>
        </SidebarHeader>

        <SidebarContent className="flex flex-1 min-h-0 flex-col overflow-hidden">
          <ScrollArea className="flex-1">
            <SidebarGroup className={cn("py-4", collapsed ? "px-2" : "px-4")}>
              <SidebarGroupContent>
                <SidebarMenu className="gap-2">
                  {outsideShell ? (
                    // Grant-only shell for outside collaborators (ENG-1820):
                    // the only surface they can use is Grants, so it is the
                    // only item. Account/logout stays in the footer.
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={BookOpen}
                      label="Grants"
                      isActive={section === "grant_suite" || section === "grants"}
                      onSelect={() => selectSection("grant_suite")}
                    />
                  ) : (
                  <>
                  <SidebarNavItem
                    collapsed={collapsed}
                    icon={LayoutDashboard}
                    label="Launch Pad"
                    isActive={section === "dashboard"}
                    onSelect={() => selectSection("dashboard")}
                    testId="sidebar-launch-pad"
                  />

                  <HistoryModuleRow
                    collapsed={collapsed}
                    icon={MessageCircle}
                    id="chat"
                    isActive={section === "chat"}
                    isOpen={expandedHistory.chat}
                    label="Research Assistant"
                    onOpenChange={(open) => setExpandedHistory((prev) => ({ ...prev, chat: open }))}
                    onSelect={() => selectSection("chat")}
                  >
                    <div
                      className={SIDEBAR_EXPANDED_CONTENT_WIDTH_CLASS}
                      data-testid="sidebar-conversations-region"
                    >
                      <ConversationsList />
                    </div>
                  </HistoryModuleRow>

                  {currentUser?.file_viewer && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={FolderOpen}
                      label="File Center"
                      isActive={section === "file_viewer"}
                      onSelect={() => selectSection("file_viewer")}
                    />
                  )}

                  {(currentUser?.grant_discover ?? (currentUser?.grant_access || currentUser?.grant_suite)) && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={BookOpen}
                      label="Grants"
                      isActive={section === "grant_suite" || section === "grants"}
                      onSelect={() => selectSection("grant_suite")}
                    />
                  )}

                  {canAccessPermitTab(currentUser, "pipeline") && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={ClipboardCheck}
                      label="Permit Suite"
                      isActive={section === "permit_suite"}
                      onSelect={() => selectSection("permit_suite")}
                    />
                  )}

                  {userHasDevelopmentSuite(currentUser) && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={FolderKanban}
                      label="Development Suite"
                      isActive={section === "development_suite"}
                      onSelect={() => selectSection("development_suite")}
                      testId="sidebar-development-suite"
                    />
                  )}

                  {canAccessPermitTab(currentUser, "plan_review") && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={DraftingCompass}
                      label="Plan Review"
                      isActive={section === "plan_review"}
                      onSelect={() => selectSection("plan_review")}
                    />
                  )}

                  {canAccessPermitTab(currentUser, "assistant") && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={Pencil}
                      label="Permit Assistant"
                      isActive={section === "form_extraction"}
                      onSelect={() => selectSection("form_extraction")}
                    />
                  )}

                  {currentUser?.trust_suite && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={Handshake}
                      label="Trust"
                      isActive={section === "trust_suite"}
                      onSelect={() => selectSection("trust_suite")}
                    />
                  )}

                  {currentUser?.staff_reports_access && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={FileText}
                      isActive={section === "staff_reports"}
                      label="Report Assistant"
                      onSelect={() => selectSection("staff_reports")}
                    />
                  )}

                  {currentUser?.connect_analytics && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={ChartNoAxesColumnIncreasing}
                      label="Connect Analytics"
                      isActive={section === "analytics"}
                      onSelect={() => selectSection("analytics")}
                    />
                  )}

                  {currentUser?.connect_analytics_live && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={ChartNoAxesColumnIncreasing}
                      label="Connect Analytics Live"
                      isActive={section === "analytics_live"}
                      onSelect={() => selectSection("analytics_live")}
                    />
                  )}

                  {currentUser?.code_library_access && (
                    <HistoryModuleRow
                      collapsed={collapsed}
                      icon={LibraryBig}
                      id="code_library"
                      isActive={section === "code_library"}
                      isOpen={expandedHistory.code_library}
                      label="Code Library"
                      onOpenChange={(open) => setExpandedHistory((prev) => ({ ...prev, code_library: open }))}
                      onSelect={() => selectSection("code_library")}
                    >
                      <div className={SIDEBAR_EXPANDED_CONTENT_WIDTH_CLASS}>
                        <CodeLibraryComparisonsList />
                      </div>
                    </HistoryModuleRow>
                  )}

                  {currentUser?.legislation_monitor_access && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={Scale}
                      label="Legislation Monitor"
                      isActive={section === "legislation_monitor"}
                      onSelect={() => selectSection("legislation_monitor")}
                    />
                  )}

                  {currentUser?.municipality_resource_links_access && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={Link2}
                      label="Resource Links"
                      isActive={section === "municipality_resource_links"}
                      onSelect={() => selectSection("municipality_resource_links")}
                    />
                  )}

                  {currentUser?.case_portal_access && (
                    <HistoryModuleRow
                      collapsed={collapsed}
                      icon={Briefcase}
                      id="case_portal"
                      isActive={section === "case_portal"}
                      isOpen={expandedHistory.case_portal}
                      label="Case Portal"
                      onOpenChange={(open) => setExpandedHistory((prev) => ({ ...prev, case_portal: open }))}
                      onSelect={() => selectSection("case_portal")}
                    >
                      <div className={SIDEBAR_EXPANDED_CONTENT_WIDTH_CLASS}>
                        <CaseHistoryList />
                      </div>
                    </HistoryModuleRow>
                  )}

                  {currentUser?.sandbox_access && (
                    <HistoryModuleRow
                      collapsed={collapsed}
                      icon={FlaskConical}
                      id="sandbox"
                      isActive={section === "sandbox"}
                      isOpen={expandedHistory.sandbox}
                      label="Sandbox"
                      onOpenChange={(open) => setExpandedHistory((prev) => ({ ...prev, sandbox: open }))}
                      onSelect={() => selectSection("sandbox")}
                    >
                      <div className={SIDEBAR_EXPANDED_CONTENT_WIDTH_CLASS}>
                        <SandboxConversationsList />
                      </div>
                    </HistoryModuleRow>
                  )}

                  {currentUser?.sandbox_access && currentUser?.can_access_sandbox_audit && (
                    <SidebarNavItem
                      collapsed={collapsed}
                      icon={ClipboardList}
                      label="Conversation Audit"
                      isActive={section === "sandbox_audit"}
                      onSelect={() => selectSection("sandbox_audit")}
                    />
                  )}
                  </>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </ScrollArea>
        </SidebarContent>

        <SidebarFooter className="border-t">
          <div className="px-4 pt-4 pb-4 space-y-2">
            {!outsideShell && (currentUser?.grant_suite || currentUser?.permit_suite || currentUser?.site_plan_intake) && (
              <ActionAlertsBell
                municipalityId={municipality?.id}
                onSelectAlert={(alert) => {
                  if (alert.source === "permit_suite") {
                    handlePermitActionAlertSelect(alert);
                  } else if (alert.source === "site_plan") {
                    navigateToPermitTab("plan_review");
                  } else {
                    handleGrantActionAlertSelect(alert);
                  }
                }}
              />
            )}
            <FooterHoverMenu
              collapsed={collapsed}
              icon={LifeBuoy}
              label="Help"
              testId="help-flyout-trigger"
            >
              <DropdownMenuItem asChild className="h-9 px-3 py-0">
                <a
                  href="https://www.ordinalforgov.com/support"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex cursor-default items-center gap-2"
                >
                  <MessagesSquare className="h-4 w-4" />
                  Support
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="h-9 px-3 py-0">
                <a
                  href="https://www.ordinalforgov.com/feedback"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex cursor-default items-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  Feedback
                </a>
              </DropdownMenuItem>
            </FooterHoverMenu>

            <FooterHoverMenu
              collapsed={collapsed}
              icon={Settings}
              label="Settings"
              testId="settings-flyout-trigger"
            >
              <DropdownMenuItem
                onSelect={() => setShowAccountDialog(true)}
                className="flex h-9 cursor-default items-center gap-2 px-3 py-0"
                data-testid="account-flyout-open-dialog"
              >
                <User className="h-4 w-4" />
                Account
              </DropdownMenuItem>
              {documentsFlagged && (
                <DropdownMenuItem
                  onSelect={() => setShowDocumentsManager(true)}
                  className="flex h-9 cursor-default items-center gap-2 px-3 py-0"
                  data-testid="settings-documents"
                >
                  <FileText className="h-4 w-4" />
                  Documents
                </DropdownMenuItem>
              )}
              {showUsersTool && (
                <DropdownMenuItem
                  onSelect={() => setShowUsersDialog(true)}
                  className="flex h-9 cursor-default items-center gap-2 px-3 py-0"
                  data-testid="settings-users"
                >
                  <Users className="h-4 w-4" />
                  Users
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => logout()}
                className="flex h-9 cursor-default items-center gap-2 px-3 py-0"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </FooterHoverMenu>

            {showTeamManagementFooter && (
              <a href="/team_management" className="mt-1 block">
                <Button
                  variant="ghost"
                  className={cn("h-9 w-full justify-start px-3 py-0", collapsed && "justify-center px-0")}
                >
                  <Users className="h-5 w-5" />
                  <span className={cn("ml-2", collapsed && "sr-only")}>Team Management</span>
                </Button>
              </a>
            )}

            {currentUser?.can_access_support_console && !currentUser?.platform_admin && (
              <a href="/madmin/support" className="mt-1 block">
                <Button
                  variant="ghost"
                  className={cn("h-9 w-full justify-start px-3 py-0", collapsed && "justify-center px-0")}
                >
                  <Settings className="h-5 w-5" />
                  <span className={cn("ml-2", collapsed && "sr-only")}>{SUPPORT_CONSOLE_LABEL}</span>
                </Button>
              </a>
            )}

            {currentUser?.platform_admin && (
              <a href="/madmin" className="mt-1 block">
                <Button
                  variant="ghost"
                  className={cn("h-9 w-full justify-start px-3 py-0", collapsed && "justify-center px-0")}
                >
                  <Settings className="h-5 w-5" />
                  <span className={cn("ml-2", collapsed && "sr-only")}>{OPS_CONSOLE_LABEL}</span>
                </Button>
              </a>
            )}
          </div>
        </SidebarFooter>
      </aside>

      {currentUser && (
        <AccountDialog
          user={currentUser}
          open={showAccountDialog}
          onOpenChange={setShowAccountDialog}
          onManageDocuments={() => setShowDocumentsManager(true)}
        />
      )}
      {municipality && (
        <DocumentsManager
          open={showDocumentsManager}
          onOpenChange={setShowDocumentsManager}
          municipalityId={municipality.id}
          catalog={documentsFlagged}
          canUpload={canManageDocuments}
        />
      )}
      <UsersSettingsModal
        open={showUsersDialog}
        onOpenChange={setShowUsersDialog}
        onTalkToCs={() => {
          setShowUsersDialog(false);
          setShowSupportRequest(true);
        }}
      />
      <SupportRequestDialog
        open={showSupportRequest}
        onOpenChange={setShowSupportRequest}
        municipalityId={municipality?.id}
        municipalityName={municipality?.name}
        source="settings_users"
        defaultCategory="account"
      />
    </SidebarRoot>
  );
}
